// Pure helpers that transform a design ({ text_fields, elements }).
import { layersInOrder, newId, withElementDefaults } from '../../utils/invitationStyle';
import { ART_OBJECTS, ART_ORNAMENTS, ART_RETIRED } from './elementArt';

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
export const round = (value, digits = 2) => {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
};

export const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Normalise #RGB / #RRGGBB to lowercase #rrggbb; null for anything else. */
export const normalizeHex = (value) => {
  if (typeof value !== 'string' || !HEX_RE.test(value.trim())) return null;
  let v = value.trim().toLowerCase();
  if (v.length === 4) v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  return v;
};

/** A colour usable as <input type=color> value. */
export const pickerValue = (value, fallback = '#1a1a1a') => normalizeHex(value) || fallback;

export const findItem = (design, id) => {
  const text = design.text_fields.find((f) => f.id === id);
  if (text) return { kind: 'text', item: text };
  const el = design.elements.find((e) => e.id === id);
  if (el) return { kind: 'element', item: el };
  return null;
};

export const updateItem = (design, id, patch) => {
  const apply = (item) => (item.id === id ? { ...item, ...(typeof patch === 'function' ? patch(item) : patch) } : item);
  return { ...design, text_fields: design.text_fields.map(apply), elements: design.elements.map(apply) };
};

export const maxZ = (design) =>
  layersInOrder(design.text_fields, design.elements).reduce((m, l) => Math.max(m, l.z), 0);

/** Give every item an explicit z equal to its paint-order rank. */
export const normalizeZ = (design) => {
  const order = layersInOrder(design.text_fields, design.elements);
  const zById = new Map(order.map((l, i) => [l.item.id, i]));
  return {
    ...design,
    text_fields: design.text_fields.map((f) => ({ ...f, z: zById.get(f.id) })),
    elements: design.elements.map((e) => ({ ...e, z: zById.get(e.id) })),
  };
};

/** Move an item one step toward the front (dir = 1) or the back (dir = -1). */
export const moveLayer = (design, id, dir) => {
  const norm = normalizeZ(design);
  const order = layersInOrder(norm.text_fields, norm.elements);
  const idx = order.findIndex((l) => l.item.id === id);
  const other = order[idx + dir];
  if (idx < 0 || !other) return design;
  const a = order[idx].z;
  const b = other.z;
  const swap = (item) => {
    if (item.id === id) return { ...item, z: b };
    if (item.id === other.item.id) return { ...item, z: a };
    return item;
  };
  return { ...norm, text_fields: norm.text_fields.map(swap), elements: norm.elements.map(swap) };
};

export const canMoveLayer = (design, id, dir) => {
  const order = layersInOrder(design.text_fields, design.elements);
  const idx = order.findIndex((l) => l.item.id === id);
  return idx >= 0 && Boolean(order[idx + dir]);
};

export const addText = (design, field) => {
  const created = {
    id: newId('custom'),
    text: 'Your text',
    x: 50,
    y: 50,
    font_size: 32,
    font_family: 'Frank Ruhl Libre',
    color: '#1A1A1A',
    text_align: 'center',
    label: 'Text',
    ...field,
    custom: true,
    z: maxZ(design) + 1,
  };
  return { design: { ...design, text_fields: [...design.text_fields, created] }, id: created.id };
};

export const addElements = (design, items) => {
  let z = maxZ(design);
  const created = items.map((item) => ({ id: newId('el'), x: 50, y: 50, ...item, z: (z += 1) }));
  return {
    design: { ...design, elements: [...design.elements, ...created] },
    id: created[created.length - 1]?.id,
  };
};

export const duplicateItem = (design, id) => {
  const found = findItem(design, id);
  if (!found) return { design, id };
  const { kind, item } = found;
  const copy = {
    ...item,
    id: newId(kind === 'text' ? 'custom' : 'el'),
    x: clamp(item.x + 3, 2, 98),
    y: clamp(item.y + 3, 2, 98),
    z: maxZ(design) + 1,
    locked: false,
    hidden: false,
  };
  if (kind === 'text') {
    copy.custom = true;
    copy.label = `${item.label || 'Text'} copy`;
    return { design: { ...design, text_fields: [...design.text_fields, copy] }, id: copy.id };
  }
  return { design: { ...design, elements: [...design.elements, copy] }, id: copy.id };
};

/** Custom text and all elements can be deleted; template text can only be hidden. */
export const isDeletable = (found) => Boolean(found && (found.kind === 'element' || found.item.custom));

export const removeItem = (design, id) => ({
  ...design,
  text_fields: design.text_fields.filter((f) => f.id !== id || !f.custom),
  elements: design.elements.filter((e) => e.id !== id),
});

// ---- Labels ----
const ART_NAMES = Object.fromEntries([...ART_OBJECTS, ...ART_ORNAMENTS, ...ART_RETIRED].map((a) => [a.src, a.name]));

export const elementLabel = (el) => {
  const e = withElementDefaults(el);
  if (e.type === 'image') return ART_NAMES[e.src] || 'Photo';
  if (e.type === 'line') return e.line_style === 'solid' ? 'Line' : `${e.line_style[0].toUpperCase()}${e.line_style.slice(1)} line`;
  if (e.type === 'ellipse') return e.fill ? 'Circle' : 'Ring';
  if (!e.fill && e.stroke_width > 0) return e.w > 60 ? 'Frame' : 'Outline box';
  if (e.rotation === 45 && e.w < 4) return 'Ornament';
  return 'Shape';
};

export const itemLabel = (found) => {
  if (!found) return '';
  return found.kind === 'text' ? found.item.label || 'Text' : elementLabel(found.item);
};

// ---- Colours ----
const HEADING_RE = /open|head|title|bsd|intro|greet|top|בס|כותרת|פתיח/i;

/** Colour role of a text field for theme recolouring. */
export const textRole = (field, topId) => {
  const key = `${field.id || ''} ${field.label || ''}`;
  if (HEADING_RE.test(key)) return 'accent';
  const size = field.font_size || 24;
  if (size >= 32) return 'ink';
  if (field.id === topId) return 'accent';
  if (size <= 16) return 'muted';
  return 'ink';
};

export const applyTheme = (design, theme) => {
  const visible = design.text_fields.filter((f) => !f.hidden);
  const top = visible.reduce((best, f) => (best && best.y <= f.y ? best : f), null);
  return {
    ...design,
    background: theme.tint ? { tint_color: theme.tint, tint_mode: 'multiply', tint_strength: 1 } : null,
    // Text on a label background keeps its colours (it contrasts with the label, not the page).
    text_fields: design.text_fields.map((f) => (f.bg_color ? f : { ...f, color: theme[textRole(f, top?.id)] })),
    elements: design.elements.map((raw) => {
      if (raw.type === 'image') return raw;
      const el = withElementDefaults(raw);
      if (el.type === 'line') return { ...raw, stroke_color: theme.frame };
      return {
        ...raw,
        ...(el.fill ? { fill: theme.frame } : {}),
        ...(el.stroke_width > 0 ? { stroke_color: theme.frame } : {}),
      };
    }),
  };
};

const TEXT_COLOR_PROPS = [
  ['color', 'Text'],
  ['stroke_color', 'Outline', (f) => f.stroke_width > 0],
  ['bg_color', 'Label'],
  ['highlight_color', 'Highlight'],
  ['shadow_color', 'Shadow'],
];
const ELEMENT_COLOR_PROPS = [
  ['fill', 'Shapes', (e) => e.type !== 'line' && e.type !== 'image'],
  ['stroke_color', 'Lines & borders', (e) => e.type === 'line' || (e.stroke_width || 0) > 0],
];

/** Unique hex colours used in the design, most used first, with where they're used. */
export const collectColors = (design) => {
  const map = new Map();
  const add = (value, where) => {
    const hex = normalizeHex(value);
    if (!hex) return;
    const entry = map.get(hex) || { hex, count: 0, where: new Set() };
    entry.count += 1;
    entry.where.add(where);
    map.set(hex, entry);
  };
  design.text_fields.forEach((f) =>
    TEXT_COLOR_PROPS.forEach(([prop, where, when]) => (!when || when(f)) && add(f[prop], where))
  );
  design.elements.forEach((e) =>
    ELEMENT_COLOR_PROPS.forEach(([prop, where, when]) => (!when || when(e)) && add(e[prop], where))
  );
  return [...map.values()]
    .sort((a, b) => b.count - a.count)
    .map((e) => ({ ...e, where: [...e.where].join(', ') }));
};

/** Replace one colour with another everywhere it is used. */
export const replaceColor = (design, from, to) => {
  const target = normalizeHex(from);
  if (!target) return design;
  const swap = (item, props) => {
    let changed = false;
    const next = { ...item };
    props.forEach(([prop]) => {
      if (normalizeHex(item[prop]) === target) {
        next[prop] = to;
        changed = true;
      }
    });
    return changed ? next : item;
  };
  return {
    ...design,
    text_fields: design.text_fields.map((f) => swap(f, TEXT_COLOR_PROPS)),
    elements: design.elements.map((e) => swap(e, ELEMENT_COLOR_PROPS)),
  };
};

// ---- Loading / saving ----
const TEMPLATE_ONLY = [
  'default_text',
  'is_required',
  'max_length',
  'ivr_prompt_text',
  'ivr_prompt_audio_url',
  'ivr_order',
  // field_input_id, input_type and field_key stay: they say which phone/SMS answer fills the box, and let the
  // server keep card fields (CVV, expiry, ...) away from the AI, also after switching templates.
];

/** Turn a template's text fields into invitation text fields. */
export const fieldsFromTemplate = (templateFields = []) =>
  templateFields.map((tf, i) => {
    const field = { ...tf, id: tf.id || `field-${i + 1}`, text: tf.default_text ?? tf.text ?? '' };
    TEMPLATE_ONLY.forEach((k) => delete field[k]);
    return field;
  });

/** Payload for POST/PUT /api/invitations: drop undefined values. */
export const cleanForSave = (items = []) =>
  items.map((item) => Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined)));

export const snapshot = (design) =>
  JSON.stringify({
    t: design.text_fields,
    e: design.elements,
    tpl: design.template_id || null,
    bg: design.background || null,
  });
